import axios from "axios";
import { logger } from "@/lib/logger";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Admin auth is now transported via HttpOnly cookies set by the backend on
// /api/admin/login. The frontend does not read or write the token — the
// browser attaches the cookie automatically as long as `withCredentials`
// is true. This closes the XSS-exfiltration vector that the previous
// sessionStorage-based approach exposed.
export const api = axios.create({
  baseURL: API,
  timeout: 15000,
  withCredentials: true,
});

// Tracking (legacy QR scan endpoint)
export async function trackScan({ page, language }) {
  const params = new URLSearchParams(window.location.search);
  try {
    await api.post("/tracking/scan", {
      page,
      language: language || "de",
      referrer: document.referrer || null,
      utm_source: params.get("utm_source"),
      utm_medium: params.get("utm_medium"),
      utm_campaign: params.get("utm_campaign"),
    });
  } catch (err) {
    // Tracking should never break UX — log for diagnostics only
    logger.warn("[trackScan] failed:", err);
  }
}

export async function fetchStats(days = 30) {
  const { data } = await api.get(`/tracking/stats?days=${days}`);
  return data;
}

export async function fetchGeoStats(days = 30) {
  const { data } = await api.get(`/admin/geo-stats?days=${days}`);
  return data;
}

export async function fetchPartnerLeads(limit = 200) {
  const { data } = await api.get(`/partner-leads?limit=${limit}`);
  return data;
}

export async function updatePartnerLead(leadId, patch) {
  const { data } = await api.patch(`/partner-leads/${leadId}`, patch);
  return data;
}

/**
 * Trigger a CSV file download for the waitlist export.
 * Uses fetch (with credentials) + Blob since axios stream handling adds complexity.
 */
export async function downloadWaitlistCsv({ newsletterOnly = false } = {}) {
  const url = `${API}/admin/export/waitlist.csv?newsletter_only=${newsletterOnly}`;
  const res = await fetch(url, {
    credentials: "include",
  });
  if (!res.ok) {
    throw new Error(`Export failed: ${res.status}`);
  }
  const blob = await res.blob();
  const disposition = res.headers.get("Content-Disposition") || "";
  const filenameMatch = /filename="([^"]+)"/.exec(disposition);
  const filename = filenameMatch ? filenameMatch[1] : `feaststar-waitlist.csv`;
  const totalRows = Number(res.headers.get("X-Total-Rows") || 0);
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objectUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(objectUrl);
  return { filename, totalRows };
}

/** Log the admin out — clears the HttpOnly session cookie server-side. */
export async function adminLogout() {
  try {
    await api.post("/admin/logout");
  } catch (err) {
    // If the request fails (e.g. already expired), the user is redirected anyway.
    logger.warn("[adminLogout] failed:", err);
  }
}
